// Catálogos clínicos compartidos entre el formulario de ficha clínica (mitad 2
// del overhaul, todavía no implementado) y los builders de exportación PDF
// (src/lib/clinicalExport/, esta mitad). Los `value` de cada catálogo son
// idénticos a los CHECKs de Postgres en
// sql/migrations/ficha_clinica_v3_certificados_2026_08_20.sql — si un
// catálogo cambia acá, hay que sincronizar el CHECK correspondiente en una
// migración nueva (y viceversa).

export interface CatalogOption {
  value: string;
  label: string;
}

export const MOTIVO_CATEGORIAS: readonly CatalogOption[] = [
  { value: 'ansiedad', label: 'Ansiedad' },
  { value: 'depresion_animo', label: 'Depresión / estado de ánimo' },
  { value: 'duelo_perdida', label: 'Duelo / pérdida' },
  { value: 'estres', label: 'Estrés' },
  { value: 'trauma', label: 'Trauma' },
  { value: 'pareja_familia', label: 'Pareja / familia' },
  { value: 'autoestima_identidad', label: 'Autoestima / identidad' },
  { value: 'habitos_conducta', label: 'Hábitos / conducta' },
  { value: 'desarrollo_personal', label: 'Desarrollo personal' },
  { value: 'crisis_urgencia', label: 'Crisis / urgencia' },
  { value: 'seguimiento_control', label: 'Seguimiento / control' },
  { value: 'otro', label: 'Otro' },
] as const;

export const CICLO_TERAPEUTICO_OPTIONS: readonly CatalogOption[] = [
  { value: 'I', label: 'Ciclo I' },
  { value: 'II', label: 'Ciclo II' },
  { value: 'III', label: 'Ciclo III' },
] as const;

// Las 8 etapas psicosociales de Erik Erikson, en orden canónico.
export const CICLO_VITAL_ETAPAS: readonly CatalogOption[] = [
  { value: 'confianza_vs_desconfianza', label: 'Confianza vs. desconfianza (0-1 año)' },
  { value: 'autonomia_vs_verguenza_duda', label: 'Autonomía vs. vergüenza y duda (1-3 años)' },
  { value: 'iniciativa_vs_culpa', label: 'Iniciativa vs. culpa (3-6 años)' },
  { value: 'laboriosidad_vs_inferioridad', label: 'Laboriosidad vs. inferioridad (6-12 años)' },
  { value: 'identidad_vs_confusion_rol', label: 'Identidad vs. confusión de rol (adolescencia)' },
  { value: 'intimidad_vs_aislamiento', label: 'Intimidad vs. aislamiento (adultez temprana)' },
  { value: 'generatividad_vs_estancamiento', label: 'Generatividad vs. estancamiento (adultez media)' },
  { value: 'integridad_vs_desesperanza', label: 'Integridad vs. desesperanza (vejez)' },
] as const;

export const CERTIFICATE_TYPES: readonly CatalogOption[] = [
  { value: 'asistencia', label: 'Certificado de asistencia' },
  { value: 'tratamiento_laboral', label: 'Certificado de tratamiento (uso laboral)' },
  { value: 'legal_judicial', label: 'Certificado para fines legales/judiciales' },
  { value: 'texto_libre', label: 'Certificado de texto libre' },
] as const;

// Fábrica de helpers de lookup — mismo criterio defensivo que
// labelPrevision/labelModalidad en clinicalExport/sharedBlocks.ts: si el
// value no matchea ningún ítem del catálogo (dato legado, catálogo
// desincronizado), se hace fallback al value crudo en vez de romper.
// Si el value viene vacío/undefined, se retorna 'Sin registro.' — mismo
// fallback que el resto de los campos clínicos en el PDF.
function makeLabelGetter(catalogo: readonly CatalogOption[]): (value?: string | null) => string {
  const map = new Map(catalogo.map((opcion) => [opcion.value, opcion.label]));
  return (value) => {
    if (!value) return 'Sin registro.';
    return map.get(value) ?? value;
  };
}

export const getMotivoCategoriaLabel = makeLabelGetter(MOTIVO_CATEGORIAS);
export const getCicloTerapeuticoLabel = makeLabelGetter(CICLO_TERAPEUTICO_OPTIONS);
export const getCicloVitalEtapaLabel = makeLabelGetter(CICLO_VITAL_ETAPAS);
export const getCertificateTypeLabel = makeLabelGetter(CERTIFICATE_TYPES);
